import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useRecoilState } from "recoil";
import { rootFixedColor, rootFixedSize } from "styles/theme";
import styled from "styled-components";
import { isSidebarAtom, isFullScreenAtom } from "styles/atoms";
import { FiLogOut } from "react-icons/fi";
import { BsCardList } from "react-icons/bs";
import { LiaToolsSolid } from "react-icons/lia";
import { LiaUsersCogSolid, LiaUserFriendsSolid } from "react-icons/lia";
import { PiBookOpenUser } from "react-icons/pi";
import { GoSidebarCollapse, GoSidebarExpand } from "react-icons/go";
import { ACCESS_TOKEN_NAME, REFRESH_TOKEN_NAME, TYPE, encryptAES256, removeLocalStorage } from "api/api";
import { AdminLoginInfoContext } from "pages/Layout";
import { IoChevronDown } from "react-icons/io5";

const HeaderContainer = styled.header`
  padding-left: 8rem;
  min-width: calc(1024px + 8rem);
  &.show-sidebar {
    min-width: calc(1024px + 13.125rem);
    padding-left: 13.125rem;
  }
  background-color: ${rootFixedColor.rootBgColor};
  color: ${rootFixedColor.rootTextColor};
  width: 100%;
  height: 3.5rem;
  padding-right: 1.25rem;
  display: flex;
  align-items: center;
  position: relative;
  z-index: 5;
  container: header-container / inline-size;
  transition: all .4s ease-out;
`;
const HeaderWrap = styled.section`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  padding-left: 1.25rem;
  > div {
    display: flex;
    align-items: center;
    flex-direction: row;
    justify-content: center;
    &:hover {
      cursor: pointer;
    }
  }
  .header-sidebar-wrap {
    width: 2rem;
    svg {
      width: 1.5rem;
      height: 1.5rem;
    }
  }
  .header-search-wrap {
    gap: 0.5rem;
    label {
      display: none;
    }
    input[type='text'] {
      padding: ${rootFixedSize.inputShortPadding};
      border-radius: ${rootFixedSize.inputBorderRadius};
      &:focus, &:focus-visible {
        outline-color: ${rootFixedColor.primaryColor};
      }
    }
    p {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 2rem;
      height: 2rem;
      border-radius: 0.25rem;
      &:hover {
        background-color: ${rootFixedColor.primaryColor};
      }
    }
  }
  .header-button-wrap {
    margin-left: auto;
    gap: 0.25rem;
  }
`;
const HeaderButton = styled.div`
  min-width: 3rem;
  height: 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
  &.mix-btn {
    padding: 0.25rem 0.5rem;
    height: 100%;
    > svg {
      width: 1rem;
    }
    span {
      font-size: 0.875rem;
    }
  }
  &:hover {
    background-color: ${rootFixedColor.primaryColor};
  }
  &.animation-btn {
    overflow: hidden;
    background-color: #494949;
    svg {
      fill: ${rootFixedColor.accentColor};
    }
    .to-left-center {
      animation: to-left-center linear .5s normal;
    }
    .to-right-center {
      animation: to-right-center linear .5s normal;
    }
  }
`;
const SidebarContainer = styled.nav`
  position: fixed;
  top: 0;
  z-index: 8;
  width: 8rem;
  height: 100vh;
  padding-bottom: 1rem;
  background-color: ${rootFixedColor.rootBgColor};
  color: ${rootFixedColor.rootTextColor};
  transition: all 0.4s ease-out;
  &.show-all {
    width: 13.125rem;
  }
  .header-logo-sidebar-icon {
    cursor: pointer;
    width: 13.125rem;
    min-width: 13.125rem;
    height: 3.5rem;
    display: flex;
    justify-content: center;
    align-items: center;
    .header-logo-wrap {
      width: 80%;
      min-width: 9.375rem;
      height: 100%;
      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }
  }
  .header-logo-short-icon {
    cursor: pointer;
    height: 3.5rem;
    display: flex;
    justify-content: center;
    align-items: center;
    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
    padding: 0.75rem;
  }
`;
const AllSidebar = styled.ul`
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  li {
    width: 100%;
  }
  > li.active {
    -webkit-box-shadow: 8px 0 0 ${rootFixedColor.primaryColor} inset;
    box-shadow: 10px 0 0 ${rootFixedColor.primaryColor} inset;
  }
  .nav-link {
    display: block;
    color: ${rootFixedColor.rootTextColor};
    padding: 0.875rem 1rem 0.875rem 1.5rem;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: 0.5rem;
    svg {
      transition: transform .8s ease;
      vertical-align: middle;
      &:not(:first-of-type){
        margin-left: auto;
      }
    }
    &:hover {
      background-color: ${rootFixedColor.primaryColor};
      cursor: pointer;
    }
  }
  a:visited {
    ${rootFixedColor.rootTextColor};
  }
`;
const ShortSidebar = styled.ul`
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  height: calc(100% - 3.25rem);
  .nav-link {
    display: block;
    color: ${rootFixedColor.rootTextColor};
    padding: 0.875rem 1rem 0.875rem 1.5rem;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: 0.5rem;
    svg {
      vertical-align: middle;
    }
    &.active {
      -webkit-box-shadow: 8px 0 0 ${rootFixedColor.primaryColor} inset;
      box-shadow: 10px 0 0 ${rootFixedColor.primaryColor} inset;
    }
    &:hover {
      background-color: ${rootFixedColor.primaryColor};
      cursor: pointer;
    }
  }
  span, li {
    font-size: 12px;
  }
`;
const ParentMenu = styled.li`
  &.nav-expanded {
    .nav-link {
      svg:last-of-type {
        transform: rotate(180deg);
      }
    }
  }
  .active {
    -webkit-box-shadow: 8px 0 0 ${rootFixedColor.primaryColor} inset;
    box-shadow: 10px 0 0 ${rootFixedColor.primaryColor} inset;
  }
  .nav-children {
    height: 0;
    overflow: hidden;
    transition: all 0.5s ease-in-out;
    li {
      a {
        height: 2.5rem;
        padding: 0.5rem 1rem 0.5rem 3rem;
        background-color: ${({theme}) => theme.newTheme.fix.secondaryBg};
      }
    }
    &.show {
      height: auto;
    }
  }
`;

type NavParentProps = {
  nameIndex: number;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  isActive?: boolean;
}

// 자식메뉴가 있는 경우 확장 관련 Fn
const NavParent: React.FC<NavParentProps> = ({ nameIndex, isExpanded, onToggle, children, isActive }) => {
  const navParentName = [
    { name: '모델 관리', ele: <BsCardList /> },
    { name: '해피콜', ele: <BsCardList /> },
  ]
  const toggleExpand = () => {
    onToggle();
  }
  return (
    <ParentMenu className={isExpanded ? 'nav-expanded' : ''}>
      <div className={`nav-link ${isActive ? 'active' : ''}`} onClick={toggleExpand}>
        <>{navParentName[nameIndex].ele}</>
        <span>{navParentName[nameIndex].name} </span>
        <IoChevronDown />
      </div>
      {isExpanded && (
        <ul className="nav-children show">
          {children}
        </ul>
      )}
    </ParentMenu>
  )
}
const SidebarAccordion: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathName = location.pathname;
  const { adminLoginInfo } = useContext(AdminLoginInfoContext);
  const currentAuthCode = Array.isArray(adminLoginInfo.auth_code) ? adminLoginInfo.auth_code : []; // 배열로 변환
  
  const [sidebarAtom, setSidebarAtom] = useRecoilState(isSidebarAtom);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const ExpandedMenu = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  }

  const type = localStorage.getItem(TYPE);
  const accessToken = localStorage.getItem(ACCESS_TOKEN_NAME);
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_NAME);
  const { REACT_APP_SECRET_KEY, REACT_APP_SECRET_IV } = process.env;
  const auth = encryptAES256(`${accessToken}||${refreshToken}||${type}`, REACT_APP_SECRET_KEY!, REACT_APP_SECRET_IV!);
  return (
    <>
      {!sidebarAtom ?
        <>
          <div className="header-logo-short-icon">
            <div>
              <img src="/image/FOCUSAI_Red_White.png" alt="short logo" onClick={() => navigate(`/admin/user/list`)} />
            </div>
          </div>
          <ShortSidebar className="nav-main">
            {currentAuthCode.includes('A') && (
              <Link className={`nav-link ${pathName.includes('/user/list') ? 'active' : ''}`} to="/admin/user/list">
                <LiaUserFriendsSolid />
                <span>고객 관리</span>
              </Link>
            )}
            {currentAuthCode.includes('C') && (
              <Link className={`nav-link ${pathName.includes('/contract/list') ? 'active' : ''}`} to="/admin/contract/list">
                <BsCardList />
                <span>계약 관리</span>
              </Link>
            )}
            {currentAuthCode.includes('D') && (
              <Link className={`nav-link ${pathName.includes('/notice') ? 'active' : ''}`} to="/admin/notice/list">
                <PiBookOpenUser />
                <span>공지사항</span>
              </Link>
            )}
            {currentAuthCode.includes('E') && (
              <Link className={`nav-link ${pathName.includes('/mgmt') ? 'active' : ''}`} to="/admin/mgmt/list">
                <LiaUsersCogSolid />
                <span>관리자 관리</span>
              </Link>
            )}
            {/* {currentAuthCode.includes('G') && (
              <Link className={`nav-link ${pathName.includes('/after-service') ? 'active' : ''}`} to="/admin/after-service">
                <LiaToolsSolid />
              </Link>
            )} */}
            {currentAuthCode.includes('H') && (
              <Link className={`nav-link ${pathName.includes('/jisa') ? 'active' : ''}`} to="/admin/jisa">
                <LiaToolsSolid />
                <span>지사 관리</span>
              </Link>
            )}
            {currentAuthCode.includes('RM') && (
              <NavParent nameIndex={0} isExpanded={activeIndex === 0} isActive={pathName.includes('/model')} onToggle={() => ExpandedMenu(0)}>
                <li><Link className={`nav-link ${pathName.includes('/model/recorder') ? 'active' : ''}`} to="/admin/model/recorder">녹화기 모델</Link></li>
                <li><Link className={`nav-link ${pathName.includes('/model/camera') ? 'active' : ''}`} to="/admin/model/camera">카메라 모델</Link></li>
              </NavParent>
            )}
            {currentAuthCode.includes('I') && (
              <NavParent nameIndex={1} isExpanded={activeIndex === 1} isActive={pathName.includes('/happy-call')} onToggle={() => ExpandedMenu(1)}>
                <li><Link className={`nav-link ${pathName.includes('/happy-call/send') ? 'active' : ''}`} to={`http://${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/admin/happy-call/send?auth=${encodeURIComponent(auth)}`}>해피콜 전송</Link></li>
                <li><Link className={`nav-link ${pathName.includes('/happy-call/template') ? 'active' : ''}`} to="/admin/happy-call/template-list">템플릿</Link></li>
              </NavParent>
            )}
          </ShortSidebar>
        </>
      :
        <>
          <div className="header-logo-sidebar-icon">
            <div className="header-logo-wrap" style={{cursor: "pointer"}}>
              <img src="/image/FOCUSAI_Red_White.png" alt="logo" onClick={() => navigate(`/admin/user/list`)} />
            </div>
          </div>
          <AllSidebar className="nav-main">
            {currentAuthCode.includes('A') && (
              <li className={`${pathName.includes('/user') ? 'active' : ''}`}>
                <Link className="nav-link" to="/admin/user/list">
                  <LiaUserFriendsSolid />
                  <span>고객 관리</span>
                </Link>
              </li>
            )}
            {currentAuthCode.includes('C') && (
              <li className={`${pathName.includes('/contract') ? 'active' : ''}`}>
                <Link className="nav-link" to="/admin/contract/list">
                  <BsCardList />
                  <span>계약 관리</span>
                </Link>
              </li>
            )}
            {currentAuthCode.includes('D') && (
              <li className={`${pathName.includes('/notice') ? 'active' : ''}`}>
                <Link className="nav-link" to="/admin/notice/list">
                  <PiBookOpenUser />
                  <span>공지사항</span>
                </Link>
              </li>
            )}
            {currentAuthCode.includes('E') && (
              <li className={`${pathName.includes('/mgmt') ? 'active' : ''}`}>
                <Link className="nav-link" to="/admin/mgmt/list">
                  <LiaUsersCogSolid />
                  <span>관리자 관리</span>
                </Link>
              </li>
            )}
            {/* {currentAuthCode.includes('G') && (
              <li className={`${pathName.includes('/after-service') ? 'active' : ''}`} >
                <Link className="nav-link" to="/admin/after-service">
                  <LiaToolsSolid />
                  <span>A/S 관리</span>
                </Link>
              </li>
            )} */}
            {currentAuthCode.includes('H') && (
              <li className={`${pathName.includes('/jisa') ? 'active' : ''}`} >
                <Link className="nav-link" to="/admin/jisa">
                  <LiaToolsSolid />
                  <span>지사 관리</span>
                </Link>
              </li>
            )}
            {currentAuthCode.includes('RM') && (
              <NavParent nameIndex={0} isExpanded={activeIndex === 0} isActive={pathName.includes('/model')} onToggle={() => ExpandedMenu(0)}>
                <li><Link className={`nav-link ${pathName.includes('/model/recorder') ? 'active' : ''}`} to="/admin/model/recorder">녹화기 모델</Link></li>
                <li><Link className={`nav-link ${pathName.includes('/model/camera') ? 'active' : ''}`} to="/admin/model/camera">카메라 모델</Link></li>
              </NavParent>
            )}
            {currentAuthCode.includes('I') && (
              <NavParent nameIndex={1} isExpanded={activeIndex === 1} isActive={pathName.includes('/happy-call')} onToggle={() => ExpandedMenu(1)}>
                <li><Link className={`nav-link ${pathName.includes('/happy-call/send') ? 'active' : ''}`} to={`http://${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/admin/happy-call/send?auth=${encodeURIComponent(auth)}`}>해피콜 전송</Link></li>
                <li><Link className={`nav-link ${pathName.includes('/happy-call/template') ? 'active' : ''}`} to="/admin/happy-call/template-list">템플릿</Link></li>
              </NavParent>
            )}
          </AllSidebar>
        </>
      }
    </>
  )
}

function Header() {
  const navigate = useNavigate();

  const [sidebarAtom, setSidebarAtom] = useRecoilState(isSidebarAtom);
  const toggleSidebar = () => {
    setSidebarAtom((prev) => !prev);
  }
  // 전체화면
  const [fullScreenAtom, setFullScreenAtom] = useRecoilState(isFullScreenAtom);

  // F11일때 전체화면 설정 막기 - F11로 제어할 때 에러 발생때문
  document.addEventListener('keydown', (e) => {
    if (e.key === "F11") e.preventDefault();
  });
  document.addEventListener("fullscreenchange", (e) => {
    if (document.fullscreenElement) setFullScreenAtom(true);
    else if (!document.fullscreenElement) setFullScreenAtom(false);
  });

  //로그아웃
  const handleLogout = async () => {
    if (window.confirm(`로그아웃하시겠습니까?`)) {
      removeLocalStorage();
      navigate('/admin/login');
    }
  }

  useEffect(() => {
    if (!fullScreenAtom && document.fullscreenElement){
      document.exitFullscreen();
    }
  }, [fullScreenAtom]);
  return (
    <>
      <HeaderContainer className={sidebarAtom ? 'show-sidebar' : ''}>
        <HeaderWrap>
          <div className="header-sidebar-wrap" onClick={toggleSidebar}>
            { sidebarAtom ? <GoSidebarExpand /> : <GoSidebarCollapse /> }
          </div>
          {/* <div className="header-search-wrap">
            <label htmlFor="selectStationDashboard"></label>
            <p><IoSearch /></p>
          </div> */}
          <div className="header-button-wrap">
            {/* <HeaderButton onClick={toggleMode} className="animation-btn">
            { darkAtom ? <MdDarkMode className="to-left-center" /> : <MdLightMode className="to-right-center" /> }
            </HeaderButton>
            <HeaderButton onClick={toggleFullScreen}>
              { !fullScreenAtom ? <AiOutlineFullscreen /> : <AiOutlineFullscreenExit /> }
            </HeaderButton> */}
            <HeaderButton onClick={() => navigate('/admin/mgmt/my-info')} className="mix-btn">
                <LiaUsersCogSolid />
              <span>내 정보</span>
            </HeaderButton>
            <HeaderButton onClick={handleLogout} className="mix-btn">
              <FiLogOut />
              <span>LOGOUT</span>
            </HeaderButton>
          </div>
        </HeaderWrap>
      </HeaderContainer>
      <SidebarContainer className={'side-menu-bar ' + (sidebarAtom ? 'show-all' : '')} >
        <SidebarAccordion />
      </SidebarContainer>
    </>
  );
}

export default Header;